/**
 * Hebrew UI strings.
 *
 * All user-facing copy lives here, including the rendering of parser warnings —
 * `src/domain` and `src/parsers` emit structured codes and never prose, so the
 * domain layer stays language-free and a second language is a second file
 * rather than a rewrite.
 */

import { formatILS } from '../domain/money.ts';
import type { DedupeReason } from '../domain/dedupe.ts';
import type { ParseWarning } from '../parsers/types.ts';

export const he = {
  app: {
    buildLabel: 'גרסה',
    buildTitle: (iso: string) => `זמן הבנייה: ${iso}`,
    title: 'הוצאות',
    tabTransactions: 'תנועות',
    tabImport: 'ייבוא',
    tabReview: 'לסיווג',
    tabSettings: 'הגדרות',
    tabSync: 'סנכרון',
    statTransactions: 'תנועות',
    statStatements: 'דפי חשבון',
    statUncategorized: 'ללא קטגוריה',
  },

  theme: {
    label: 'ערכת נושא',
    light: 'בהיר',
    dark: 'כהה',
    system: 'לפי המערכת',
  },

  transactions: {
    newImport: 'חדש',
    clearImportHighlights: 'ניקוי סימון תנועות חדשות',
    loading: 'טוען…',
    empty: 'אין עדיין תנועות — ייבאו דף חשבון כדי להתחיל.',
    searchPlaceholder: 'חיפוש בית עסק, הערה או מספר כרטיס…',
    clearSearch: 'ניקוי החיפוש',
    everyone: 'כולם',
    everywhere: 'ישראל וחו״ל',
    regionFilter: 'סינון לפי מיקום',
    regionIsrael: 'ישראל',
    regionAbroad: 'חו״ל',
    allCategories: 'כל הקטגוריות',
    categorySearch: 'חיפוש קטגוריה…',
    categoryNoMatch: 'אין קטגוריה מתאימה',
    categoryFilterLabel: 'סינון לפי קטגוריה',
    allMonths: 'כל החודשים',
    monthsSelected: (n: number) => `${n} חודשים`,
    monthFilterLabel: 'סינון לפי חודשים',
    clearMonths: 'נקה',
    selectYear: 'בחר הכל',
    noMonths: 'אין חודשים',
    rows: 'שורות',
    dateRange: (from: string, to: string) => (from === to ? from : `${from}–${to}`),
    exportCsv: 'ייצוא CSV',
    exportCsvHint: 'הורדת התנועות לפי הסינון הנוכחי',
    exportYes: 'כן',
    exportNo: 'לא',
    exportColumns: {
      date: 'תאריך עסקה',
      chargeDate: 'מועד חיוב',
      merchant: 'בית עסק',
      amount: 'סכום בש״ח',
      originalAmount: 'סכום מקורי',
      currency: 'מטבע',
      category: 'קטגוריה',
      subcategory: 'תת-קטגוריה',
      person: 'אדם',
      card: 'כרטיס',
      region: 'מיקום',
      notes: 'הערות',
      details: 'פירוט',
      installment: 'תשלום',
      installmentOf: 'מתוך',
      purchaseTotal: 'סכום העסקה המלא',
      standingOrder: 'הוראת קבע',
      refund: 'זיכוי',
      excluded: 'מוחרג מדוחות',
    },
    colDate: 'תאריך',
    colMerchant: 'בית עסק',
    colAmount: 'סכום',
    colCard: 'כרטיס',
    colNotes: 'הערות',
    notesEdit: 'הערה חופשית — נשמרת מיד. הטקסט האפור הוא הפירוט מדף החשבון ואינו נמחק.',
    colCategory: 'קטגוריה',
    uncategorized: 'ללא קטגוריה',
    selected: (n: number) => `${n} נבחרו`,
    clearSelection: 'ביטול בחירה',
    assignCategory: 'שיוך לקטגוריה',
    excludeFromReports: 'החרגה מדוחות',
    includeInReports: 'החזרה לדוחות',
    selectAll: 'בחירת הכול',
    deleteSelected: 'מחיקה',
    confirmDelete: (n: number) =>
      `למחוק ${n} תנועות לצמיתות? הפעולה לא ניתנת לביטול. ייבוא חוזר של אותו דף חשבון יחזיר אותן.`,
    standingOrder: 'הוראת קבע',
    installmentTotal: 'סה"כ',

    addManual: 'הוספת תנועה',
    addManualTitle: 'הוספת תנועה ידנית',
    addManualHint: 'תנועה שלא הגיעה מדף חשבון — למשל מזומן, או שורה שנמחקה בטעות.',
    manualDate: 'תאריך עסקה',
    manualChargeDate: 'מועד חיוב',
    manualChargeDateHint: 'ריק = לפי תאריך העסקה',
    manualMerchant: 'בית עסק',
    manualAmount: 'סכום ב-₪',
    manualAmountHint: 'סכום שלילי = זיכוי',
    manualCard: 'כרטיס',
    manualCategory: 'קטגוריה',
    manualNotes: 'הערות',
    manualInstallments: 'תשלומים',
    manualInstallmentCurrent: 'תשלום',
    manualInstallmentTotal: 'מתוך',
    manualInstallmentAmount: 'סכום העסקה המלא',
    manualInstallmentHint: 'הסכום למעלה הוא החיוב החודשי; כאן הסכום המלא של הרכישה.',
    manualStandingOrder: 'הוראת קבע',
    manualSave: 'הוספה',
    manualCancel: 'ביטול',
    manualNoCards: 'צריך כרטיס אחד לפחות לפני הוספת תנועה. אפשר להוסיף כרטיס במסך ההגדרות.',
    manualAdded: (merchant: string) => `נוספה תנועה: ${merchant}`,
    manualErrorMerchant: 'צריך למלא שם בית עסק.',
    manualErrorAmount: 'צריך למלא סכום שאינו אפס.',
    manualErrorDate: 'צריך למלא תאריך עסקה תקין.',
    manualErrorInstallment: 'תשלום נוכחי חייב להיות בין 1 למספר התשלומים.',
  },

  import: {
    dropHere: 'גררו לכאן דף חשבון',
    dropHint: '‏.xlsx‏, .xls, .csv או ‎.pdf — נתמכים ישראכרט, כאל, בנק הפועלים ובנק קפריסין',
    chooseFile: 'בחירת קובץ',
    reading: (fileName: string) => `קורא את ${fileName}…`,
    done: (count: number, fileName: string) => `יובאו ${count} תנועות מתוך ${fileName}.`,
    exchangeRateUnavailable: (detail: string) =>
      `לא ניתן לקבל את שער האירו העדכני מבנק ישראל; הייבוא לא בוצע כדי לא לשמור סכומים שגויים. ${detail}`,

    newCard: (last4: string) => `כרטיס חדש ···${last4}`,
    newBankAccount: (last4: string) => `חשבון בנק חדש ···${last4}`,
    newCardExplain: 'הכרטיס הזה לא הופיע קודם. בדף החשבון רשום שהוא של',
    newCardExplainTail: '. אשרו למי הוא שייך — נשאל פעם אחת בלבד ואז ייזכר.',
    newBankAccountExplain: 'החשבון הזה לא הופיע קודם. בחרו למי הוא שייך — נשאל פעם אחת בלבד ואז ייזכר.',
    unnamedHolder: 'בעל כרטיס לא מזוהה',
    person: 'אדם',
    newPerson: '➕ אדם חדש…',
    name: 'שם',
    cardNickname: 'כינוי לכרטיס',
    bankAccountNickname: 'כינוי לחשבון',
    last4: '4 ספרות אחרונות',
    bankAccountLast4: '4 ספרות אחרונות של החשבון',
    continue: 'המשך',
    cancel: 'ביטול',

    statNew: 'חדשות',
    statDuplicates: 'כפילויות',
    statReview: 'לבדיקה',
    statUnreadable: 'לא נקראו',

    alreadyImported: (date: string) =>
      `קובץ עם תוכן זהה כבר יובא ב־${date}. ייבוא חוזר בטוח — כל השורות יזוהו ככפילויות.`,
    warningsSummary: (count: number) =>
      count === 1 ? 'אזהרה אחת מהמפענח' : `${count} אזהרות מהמפענח`,
    row: (rowNumber: number) => `שורה ${rowNumber}`,
    includeReview: (count: number) => `לייבא גם את ${count} השורות שסומנו לבדיקה`,
    importButton: (count: number) => `ייבוא ${count} תנועות`,
    nothingSavedYet: 'שום דבר לא נשמר עד לאישור.',

    statusNew: 'חדשה',
    statusDuplicate: 'כפילות',
    statusReview: 'לבדיקה',

    reasonAlreadyImported: 'כבר קיימת במאגר.',
    reasonRepeatedInFile: 'מופיעה פעמיים בקובץ הזה עם אותה זהות.',
    reasonLookalikeNewReference:
      'אותו תאריך, סכום ובית עסק כמו תנועה שכבר יובאה, אבל עם מספר שובר אחר. ' +
      'לייבא רק אם מדובר ברכישה שנייה אמיתית.',
    reasonLookalikeNoReference:
      'אותו תאריך, סכום ובית עסק כמו תנועה שכבר יובאה. הקובץ הזה לא מדפיס מספר שובר, ' +
      'ולכן אי אפשר לוודא שזו אותה תנועה. לייבא רק אם מדובר ברכישה שנייה אמיתית.',
    reasonSamePeriodLookalike:
      'אותו תאריך, סכום ובית עסק כמו תנועה שכבר יובאה מקובץ אחר של אותו חיוב.',
    reasonPeriodAlreadyImported:
      'בתאריך הזה כבר יובאו תנועות מאותו חודש חיוב. אותה תנועה יכולה להיראות אחרת ב-PDF ובאקסל, ' +
      'ולכן השורה נשמרה לבדיקה.',

    samePeriod: (fileName: string, chargeDate: string) =>
      `חיוב ${chargeDate} בכרטיס הזה כבר יובא מהקובץ ${fileName}. ` +
      'ה-PDF והאקסל של ישראכרט לא חולקים מספר שובר ומקצרים שמות בתי עסק אחרת, ' +
      'ולכן שורות שלא הותאמו בתאריכים שכבר יובאו סומנו לבדיקה. ' +
      'שורות מתאריכים חדשים יזוהו כתוספות חדשות.',

    historyTitle: 'קבצים שיובאו',
    historyFile: 'קובץ',
    historyCard: 'מקור חיוב',
    historyImported: 'יובאו',
    historySkipped: 'דולגו',
    undoImport: 'ביטול ייבוא',
    undone: (removed: number, fileName: string) => `הוסרו ${removed} שורות מתוך ${fileName}.`,
    undoneKept: (removed: number, kept: number) =>
      `הוסרו ${removed} שורות. ${kept} שורות שנערכו ידנית נשמרו.`,
    forceUndo: (kept: number) => `מחיקת ${kept} השורות שנערכו ידנית`,
    forceUndoConfirm: (kept: number) =>
      `למחוק גם ${kept} שורות שנערכו ידנית? הפעולה לא ניתנת לביטול.`,

    unknownFormat:
      'לא זוהה פורמט של דף החשבון. נתמכים ישראכרט וכאל, תנועות בחשבון באקסל ודו״ח ניהול תקציב ב-PDF של בנק הפועלים, ופעילות חשבון באירו של בנק קפריסין.',
    noRows: 'הקובץ זוהה אך לא נמצאו בו שורות תנועה קריאות.',
    unreadableFile: (detail: string) => `לא ניתן לקרוא את הקובץ: ${detail}`,
  },

  sync: {
    title: 'סנכרון OneDrive',
    signIn: 'התחברות ל-OneDrive',
    signOut: 'התנתקות',
    signedInAs: (name: string) => `מחובר כ-${name}`,
    notConfigured:
      'סנכרון OneDrive לא הוגדר. הגדירו VITE_MSAL_CLIENT_ID בקובץ ‎.env — ראו את ה-README.',
    neverSynced: 'עדיין לא סונכרן',
    lastSynced: (when: string) => `סונכרן לאחרונה ${when}`,
    syncNow: 'סנכרון עכשיו',
    syncing: 'מסנכרן…',
    redirecting: 'מעביר לדף ההתחברות של מיקרוסופט…',
    redirectNote:
      'ההתחברות מתבצעת בניווט מלא לדף של מיקרוסופט ולא בחלון קופץ. הדף ייטען מחדש בסיום — ייבוא שלא אושר יאבד, לכן כדאי להתחבר לפני שמתחילים ייבוא.',
    pushed: (count: number) => `הועלו ${count} תנועות.`,
    pulled: (count: number) => `התקבלו ${count} תנועות חדשות.`,
    upToDate: 'הכול מעודכן.',
    offline: 'אין חיבור לאינטרנט — הנתונים נשמרים מקומית ויסונכרנו בהמשך.',
    pendingWrites: (count: number) => `${count} שינויים ממתינים לסנכרון.`,
    error: (detail: string) => `הסנכרון נכשל: ${detail}`,

    authExpired: 'תוקף ההתחברות ל-OneDrive פג. הנתונים נשמרים מקומית — התחברו מחדש כדי לסנכרן.',
    reauthenticate: 'התחברות מחדש',

    conflictTitle: 'התגלתה התנגשות',
    conflictTransactions: (remote: number, local: number) =>
      `במכשיר אחר יש ${remote} תנועות שאין כאן, וכאן יש ${local} שאין שם. תנועות מתמזגות בבטחה — אף רשומה לא תימחק.`,
    conflictConfig:
      'ההגדרות (אנשים, כרטיסים, קטגוריות, כללים) שונו בשני מקומות. בחרו איזו גרסה לשמור.',
    keepLocal: 'לשמור את הגרסה המקומית',
    keepRemote: 'לשמור את הגרסה מ-OneDrive',
    mergeTransactions: 'מיזוג תנועות',
    futureSchema:
      'הנתונים ב-OneDrive נכתבו בגרסה חדשה יותר של האפליקציה. רעננו את הדף לפני שממשיכים — כתיבה עכשיו תמחק שדות שהגרסה הזו לא מכירה.',
  },

  review: {
    title: 'תור סיווג',
    empty: 'כל התנועות מסווגות. 🎉',
    remaining: (n: number) => `${n} תנועות ממתינות לסיווג`,
    pickCategory: 'בחרו קטגוריה',
    createRule: 'יצירת כלל לבית העסק הזה',
    createRuleHint: (merchant: string, count: number) =>
      `כלל חדש עבור "${merchant}" יסווג גם ${count} תנועות קודמות`,
    skip: 'דילוג',
    undo: 'ביטול הפעולה האחרונה',
    keyboardHint: 'מקשים: ↑↓ מעבר · 1-9 שיוך מהיר · R יצירת כלל · S דילוג · U ביטול',
    applied: (n: number) => `סווגו ${n} תנועות`,
  },

  settings: {
    title: 'הגדרות',
    tabPeople: 'אנשים',
    tabCards: 'כרטיסים',
    tabCategories: 'קטגוריות',
    tabRules: 'כללי סיווג',

    people: 'אנשים',
    addPerson: 'הוספת אדם',
    personName: 'שם',
    personColor: 'צבע',
    personAliases: 'שמות בדפי חשבון',
    aliasHint: 'שמות בעל הכרטיס כפי שהם מופיעים בדפי החשבון, מופרדים בפסיק. לפי אלה משויך דף חשבון חדש אוטומטית.',
    deletePerson: 'מחיקה',
    movePersonTo: 'העברת הכרטיסים והתנועות אל',
    personCardCount: (n: number) => `${n} כרטיסים`,
    cannotDeletePerson: (cards: number, transactions: number) =>
      `אי אפשר למחוק — לאדם הזה משויכים ${cards} כרטיסים ו-${transactions} תנועות. בחרו קודם אדם להעביר אליהם.`,

    cards: 'כרטיסים',
    cardNickname: 'כינוי',
    cardOwner: 'בעלים',
    cardOwnerMissing: '— הבעלים נמחק, בחרו מחדש —',
    cardIssuer: 'מנפיק',
    cardLast4: '4 ספרות',
    cardRegion: 'מיקום ההוצאות',
    regionIsrael: 'ישראל',
    regionAbroad: 'חו״ל',
    cannotDeleteCard: (n: number) =>
      `אי אפשר למחוק — לכרטיס הזה משויכות ${n} תנועות. בטלו קודם את הייבוא.`,

    cleanupDuplicates: 'ניקוי ומיזוג תנועות כפולות',
    cleanupOrphans: 'ניקוי ומיזוג ייבוא יתום',
    cleanupNothing: 'לא נמצאו תנועות כפולות או יתומות לניקוי.',
    cleanupDone: (removed: number, orphans: number) =>
      orphans === 0
        ? `נוקו ${removed} תנועות כפולות.`
        : `נוקו ${removed} תנועות כפולות. ${orphans} תנועות משויכות לכרטיס שנמחק ולא נגענו בהן — שייכו אותן לכרטיס קיים.`,
    attributionRepaired: (rows: number) =>
      rows === 0
        ? 'שיוך התנועות לאנשים תקין.'
        : `תוקן השיוך של ${rows} תנועות לפי הכרטיס שלהן.`,
    attributionDangling: (n: number) =>
      `${n} כרטיסים משויכים לאדם שנמחק. בחרו להם בעלים כאן, והתנועות יעברו איתם.`,
    repairAttribution: 'תיקון שיוך לאנשים',

    categories: 'קטגוריות',
    addCategory: 'הוספת קטגוריה',
    labelHe: 'שם בעברית',
    labelEn: 'שם באנגלית',
    parent: 'קטגוריית אב',
    noParent: '— קטגוריה ראשית —',
    kind: 'סוג',
    kindFixed: 'קבועה',
    kindVariable: 'משתנה',
    need: 'חיוניות',
    needEssential: 'חיונית',
    needDiscretionary: 'לא חיונית',
    deleteCategory: 'מחיקה',
    moveTo: 'העברת התנועות אל',
    deleteCategoryConfirm: (n: number) => `${n} תנועות משויכות לקטגוריה הזו. לאן להעביר אותן?`,

    rules: 'כללי סיווג',
    ruleName: 'שם',
    ruleMatch: 'תנאי',
    ruleCategory: 'קטגוריה',
    ruleEnabled: 'פעיל',
    ruleMatches: (n: number) =>
      n === 1 ? 'תואם תנועה אחת' : `תואם ${n} תנועות`,
    ruleEdit: 'עריכה',
    ruleDelete: 'מחיקה',
    addRule: 'הוספת כלל',
    newRule: 'כלל חדש',
    ruleDrag: 'גרירה לשינוי הסדר · ↑↓ במקלדת',
    ruleNewHint: 'הכלל יתווסף בראש הרשימה, לפני הכללים הקיימים. אפשר לגרור אותו אחר כך.',
    ruleModeSubstring: 'מכיל',
    ruleModeRegex: 'ביטוי רגולרי',
    ruleMerchantHint:
      'הטקסט מושווה לשם בית העסק אחרי נורמליזציה — אותיות גדולות, בלי סימני פיסוק וסניפים.',
    ruleRegexHint:
      'ביטוי רגולרי מול אותו שם מנורמל. הסימן | מפריד בין חלופות. אין צורך בלוכסנים בהתחלה ובסוף.',
    ruleRegexInvalid: 'הביטוי אינו תקין. כלל כזה לא יתאים לאף תנועה.',
    ruleNeedsMatcher: 'צריך תנאי. כלל בלי תנאי מתאים לכל התנועות.',
    ruleMatchesHint: 'ספירה של הכלל לבדו, בלי קשר לסדר — כלל מוקדם יותר עשוי לתפוס חלק מהן.',
    reapplyRules: 'הרצת כללים מחדש',
    reapplied: (n: number) => `סווגו מחדש ${n} תנועות`,
    rulesHint: 'הכללים נבדקים לפי הסדר, והראשון שמתאים קובע. כלל ספציפי חייב להופיע לפני כלל כללי ממנו.',
    save: 'שמירה',
    cancel: 'ביטול',
    add: 'הוספה',
  },

  charts: {
    tabDashboard: 'סקירה',
    tabTrends: 'מגמות',
    tabCategories: 'קטגוריות',
    tabMerchants: 'בתי עסק',
    tabPeople: 'השוואה',
    tabNeed: 'חיוניות',

    needEssential: 'חיוניות',
    needDiscretionary: 'לא חיוניות',
    needUnclassified: 'ללא סיווג',
    needSplit: 'חיוני מול לא חיוני',
    discretionaryShare: 'חלק לא חיוני',
    topDiscretionary: 'הוצאות לא חיוניות מובילות',
    needHint:
      'הסיווג נקבע לפי הקטגוריה, וקטגוריית משנה גוברת על קטגוריית האב. אפשר לשנות אותו בהגדרות ← קטגוריות.',
    needUnclassifiedNote: (amount: string) =>
      `${amount} עדיין ללא סיווג ולא נכללים באף צד.`,

    thisMonth: 'החודש',
    lastMonth: 'חודש קודם',
    average12: 'ממוצע 12 חודשים',
    vsLastMonth: 'מול חודש קודם',
    vsAverage: 'מול הממוצע',
    projected: 'תחזית לסוף החודש',
    topCategories: 'קטגוריות מובילות',
    monthlySpend: 'הוצאה חודשית',
    movingAverage: 'ממוצע נע 3 חודשים',
    byCategory: 'לפי קטגוריה',
    topMerchants: 'בתי עסק מובילים',
    recurring: 'חיובים חוזרים',
    annualized: 'עלות שנתית',
    frequency: 'מספר חיובים',
    householdTotal: 'סה"כ משק בית',
    total: 'סה"כ בטווח',
    monthlyAverage: 'ממוצע חודשי',
    highestMonth: 'החודש הגבוה',
    other: 'אחר',
    back: 'חזרה',
    perYear: 'לשנה',

    committed: 'כבר מחויב',
    committedOutstanding: 'יתרת תשלומים פתוחים',
    committedNextChargeLabel: (month: string) => `לחיוב ב${month}`,
    committedRecurringLabel: 'חיובים חוזרים לחודש',
    committedRemaining: (remaining: number, total: number) =>
      remaining === 1
        ? `תשלום אחרון מתוך ${total}`
        : `נותרו ${remaining} תשלומים מתוך ${total}`,
    committedProjected: 'הערכה',
    committedEmpty: 'אין תשלומים פתוחים או חיובים חוזרים.',
    committedHint:
      'יתרת התשלומים בעסקאות תשלומים שטרם הסתיימו. תשלומים שכבר נקבעו בדף מדויקים; תשלומים שטרם הופיעו מוערכים לפי החיוב החודשי. לא כולל רכישות חדשות.',
    committedNextChargeHint:
      'סך החיובים שתאריך החיוב שלהם עוד לא עבר — כלומר החשבון הבא, כולל רכישות שכבר בוצעו וטרם חויבו.',
    committedRecurringHint:
      'הערכה לפי חיובים חוזרים שזוהו — אין להם מועד סיום ידוע, ולכן הם לא מחוברים ליתרת התשלומים.',

    priceChanges: 'הוראות קבע שהמחיר שלהן השתנה',
    priceChangePerYear: (amount: string) => `${amount} לשנה`,
    priceChangeSince: (date: string) => `מאז ${date}`,
    priceChangesEmpty: 'לא זוהו שינויי מחיר בהוראות הקבע.',
    priceChangesHint:
      'הוראות קבע בקצב חודשי שהמחיר שלהן עבר מרמה אחת לאחרת. נדרשים לפחות שני חיובים במחיר הקודם, ולכן הוראת קבע בסכום משתנה — חשמל, למשל — לא נכללת.',

    freshness: 'עדכניות הנתונים',
    freshUpTo: (date: string) => `נתונים עד ${date}`,
    freshAge: (days: number) =>
      days <= 0
        ? 'מעודכן להיום'
        : days === 1
          ? 'לפני יום'
          : days === 2
            ? 'לפני יומיים'
            : `לפני ${days} ימים`,
    freshCardLag: (card: string, date: string) => `${card} מעודכן עד ${date} בלבד`,
    freshWarning: 'ייתכן שחסר ייבוא, ולכן הסכומים של החודש חלקיים.',
    noData: 'אין מספיק נתונים להצגה',
    amountBasis: 'בסיס סכום',
    basisCashflow: 'תזרים (חיוב חודשי)',
    basisPurchase: 'רכישה (סכום מלא)',
    dateRange: 'טווח תאריכים',
    categoriesSelected: (n: number) => `${n} קטגוריות`,
    clearCategories: 'ניקוי',
    categoryFilter: 'סינון לפי קטגוריה',
    householdByCategory: 'סה"כ משק בית לפי קטגוריה',
    rangeThisMonth: 'החודש',
    rangeYtd: 'מתחילת השנה',
    range12m: '12 חודשים אחרונים',
    rangeAll: 'הכול',
  },
} as const;

/* ---------------------------------------------------------------- warnings */

export function formatWarning(warning: ParseWarning): string {
  switch (warning.code) {
    case 'missing-meta':
      switch (warning.field) {
        case 'charge-date':
          return 'לא נמצא תאריך חיוב מפורש; נעשה שימוש ב-1 בחודש החיוב. דיווח תזרים לפי תאריך חיוב עשוי לסטות בכמה ימים.';
        case 'card-number':
          return 'לא נמצא מספר הכרטיס בדף החשבון — תתבקשו לבחור כרטיס.';
        case 'cardholder':
          return 'לא נמצא שם בעל הכרטיס — תתבקשו לציין למי שייך דף החשבון.';
      }
      return '';

    case 'billed-not-ils':
      return `מטבע החיוב הוא ${warning.currency} ולא שקל. הסכום נשמר כפי שהוא, ללא המרה.`;

    case 'foreign-installment':
      return 'תשלומים במטבע חוץ: סכום העסקה המלא חושב כמכפלת החיוב החודשי במספר התשלומים. בדקו מול דף החשבון לפני הסתמכות על תצוגת "לפי רכישה".';

    case 'installment-mismatch':
      return `תשלום ${warning.current} מתוך ${warning.total}: החיוב החודשי ${formatILS(
        warning.charge,
      )} לא מתחלק בסכום העסקה ${formatILS(warning.statedTotal)}. נשמר כפי שהופיע.`;

    case 'ambiguous-currency':
      return `סימן המטבע "${warning.symbol}" אינו חד-משמעי; נרשם כ-${warning.resolved}. ודאו זאת לפני הסתמכות על הסכום המקורי.`;

    case 'amount-mismatch':
      return `סכום העסקה ${formatILS(warning.dealAmount)} שונה מסכום החיוב ${formatILS(
        warning.billedAmount,
      )} ללא סימון תשלומים. נעשה שימוש בסכום החיוב; כדאי לבדוק את השורה.`;

    case 'unparsed-row':
      return 'השורה דולגה: לא נמצאו בה תאריך או סכום קריאים.';

    case 'total-mismatch':
      return `סכום השורות שנקראו ${formatILS(warning.parsedSum)} אך בדף החשבון רשום ${formatILS(
        warning.statedTotal,
      )}. ייתכן ששורות חסרות או נקראו לא נכון.`;

    case 'skipped-card-charge':
      return warning.count === 1
        ? 'חיוב אחד של כרטיס אשראי הוחרג כדי לא לייבא אותו פעמיים.'
        : `${warning.count} חיובי כרטיס אשראי הוחרגו כדי לא לייבא אותם פעמיים.`;
  }
}

/* ------------------------------------------------------------ dedupe reasons */

export function formatDedupeReason(reason: DedupeReason): string {
  switch (reason.code) {
    case 'already-imported':
      return he.import.reasonAlreadyImported;
    case 'repeated-in-file':
      return he.import.reasonRepeatedInFile;
    case 'lookalike-new-reference':
      return he.import.reasonLookalikeNewReference;
    case 'lookalike-no-reference':
      return he.import.reasonLookalikeNoReference;
    case 'same-period-lookalike':
      return he.import.reasonSamePeriodLookalike;
    case 'period-already-imported':
      return he.import.reasonPeriodAlreadyImported;
  }
}

export const t = he;
